import React, { useEffect, useState } from 'react';
import { fetchCandidates, createCandidate, updateCandidate, deleteCandidate, filterCandidates } from './candidateApi';
import { fetchMedicalColleges } from './api';
import CustomNavbar from '../CustomPermissions/CustomNavbar';

const MedicalCollegeCandidateList = () => {
    const [candidates, setCandidates] = useState([]);
    const [medicalColleges, setMedicalColleges] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedCandidateId, setExpandedCandidateId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

    // Get the currently logged-in user ID and superuser flag from localStorage (set at login)
    const currentUserId = parseInt(localStorage.getItem('user_id'), 10);
    const isSuperuser = localStorage.getItem('is_superuser') === 'true';

    // Helper: returns true if the logged-in user is the one who created this candidate
    const isCreatedByMe = (candidate) => {
        // created_by is the FK integer ID returned by the backend
        return candidate.created_by !== null &&
            candidate.created_by !== undefined &&
            Number.isFinite(currentUserId) &&
            candidate.created_by === currentUserId;
    };
    const [currentCandidate, setCurrentCandidate] = useState({
        name: '',
        medical_college: '',
        passing_year: '',
        passing_month: '',
        contact_number: '',
        preferred_location: '',
        state: '',
        current_city: '',
        native_city: '',
        speciality: '',
        spouse_speciality: '',
    });
    const [filters, setFilters] = useState({
        speciality: '',
        medical_college: '',
        passing_year: '',
        passing_month: '',
        state: '',
        current_city: '',
        preferred_location: ''
    });

    // Fetch candidates and medical colleges
    useEffect(() => {
        const getData = async () => {
            try {
                setIsLoading(true);
                const [candidatesResponse, collegesResponse] = await Promise.all([
                    fetchCandidates(),
                    fetchMedicalColleges()
                ]);
                setCandidates(candidatesResponse.data);
                setMedicalColleges(collegesResponse.data);
                setError(null);
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Failed to load data. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };
        getData();
    }, []);

    // Handle delete candidate
    const handleDelete = async (id) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this candidate?');
        if (confirmDelete) {
            try {
                await deleteCandidate(id);
                setCandidates(candidates.filter((c) => c.id !== id));
                setExpandedCandidateId(null);
            } catch (error) {
                console.error('Error deleting candidate:', error);
                const msg = error?.response?.data?.detail || 'Failed to delete candidate. Please try again.';
                alert(msg);
            }
        }
    };

    // Open modal for creating/editing a candidate
    const openModal = (mode, candidate = null) => {
        setModalMode(mode);
        setCurrentCandidate(mode === 'create'
            ? {
                name: '',
                medical_college: '',
                passing_year: '',
                passing_month: '',
                contact_number: '',
                preferred_location: '',
                state: '',
                current_city: '',
                native_city: '',
                speciality: '',
                spouse_speciality: '',
            }
            : { ...candidate }
        );
        setIsModalOpen(true);
    };

    // Handle modal submission
    const handleModalSubmit = async (e) => {
        e.preventDefault();
        try {
            if (modalMode === 'create') {
                const response = await createCandidate(currentCandidate);
                setCandidates([...candidates, response.data]);
            } else {
                const response = await updateCandidate(currentCandidate.id, currentCandidate);
                setCandidates(candidates.map(c =>
                    c.id === currentCandidate.id ? response.data : c
                ));
                setExpandedCandidateId(null);
            }
            setIsModalOpen(false);
        } catch (error) {
            console.error(`Error ${modalMode === 'create' ? 'creating' : 'updating'} candidate:`, error);
            alert(`Failed to ${modalMode === 'create' ? 'create' : 'update'} candidate. Please try again.`);
        }
    };

    // Handle input changes in the modal
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        let newSpeciality = currentCandidate.speciality;
        if (name === 'medical_college') {
            const selectedCollege = medicalColleges.find(c => c.id.toString() === value.toString());
            newSpeciality = selectedCollege ? selectedCollege.specialization : '';
        }

        setCurrentCandidate(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'medical_college' && { speciality: newSpeciality })
        }));
    };

    // Toggle expanded row for additional details
    const toggleExpandedRow = (id) => {
        setExpandedCandidateId(expandedCandidateId === id ? null : id);
    };

    // Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Apply filters
    const handleFilter = async () => {
        try {
            setIsLoading(true);
            const response = await filterCandidates(filters);
            setCandidates(response.data.results);
            setError(null);
        } catch (error) {
            console.error('Error filtering candidates:', error);
            setError('Failed to filter candidates. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Reset filters
    const resetFilters = async () => {
        setFilters({
            speciality: '',
            medical_college: '',
            passing_year: '',
            passing_month: '',
            state: '',
            current_city: '',
            preferred_location: ''
        });
        try {
            setIsLoading(true);
            const response = await fetchCandidates();
            setCandidates(response.data);
            setError(null);
        } catch (error) {
            console.error('Error resetting filters:', error);
            setError('Failed to reset filters. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Get medical college name by ID
    const getCollegeName = (collegeId) => {
        const college = medicalColleges.find(c => c.id === collegeId);
        return college ? college.name : 'N/A';
    };


    // Error state
    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <h3 className="text-red-700 font-semibold mb-2">Error</h3>
                <p className="text-red-600">{error}</p>
            </div>
        );
    }

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return (
        <div style={{ display: 'flex', flexDirection: 'row', minHeight: '100vh' }}>
            <CustomNavbar />
            <div className="container mx-auto px-4 py-4">
                {/* Candidate Modal */}
                {isModalOpen && (
                    <div
                        className="fixed inset-0 flex justify-center items-center z-50"
                        id="candidatemodalopen"
                        style={{ width: "100vw", height: "100vh" }}
                    >
                        <div
                            className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm"
                            style={{
                                width: "100vw",
                                height: "150%",
                                background: "black",
                                backgroundSize: "200% 200vh",
                                overflowY: "hidden",
                                paddingBottom: "150px",
                            }}
                        ></div>
                        <div
                            className="relative bg-white rounded-lg shadow-lg"
                            style={{
                                height: "95vh",
                                overflowY: "auto",
                                width: "60vw",
                            }}
                        >
                            <div
                                className="overflow-auto"
                                style={{ padding: "100px", paddingTop: "20px" }}
                            >
                                <h2 className="text-center text-blue-700 mb-5">
                                    {modalMode === 'create' ? 'Add Candidate' : 'Edit Candidate'}
                                </h2>
                                <form onSubmit={handleModalSubmit} className="space-y-4">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                            Name *
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={currentCandidate.name}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="medical_college" className="block text-sm font-medium text-gray-700 mb-2">
                                            Medical College *
                                        </label>
                                        <select
                                            id="medical_college"
                                            name="medical_college"
                                            value={currentCandidate.medical_college}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Select Medical College</option>
                                            {medicalColleges.map(college => (
                                                <option key={college.id} value={college.id}>
                                                    {college.name} - {college.city}, {college.state}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="passing_year" className="block text-sm font-medium text-gray-700 mb-2">
                                                Passing Year *
                                            </label>
                                            <input
                                                type="number"
                                                id="passing_year"
                                                name="passing_year"
                                                value={currentCandidate.passing_year}
                                                onChange={handleInputChange}
                                                required
                                                min="1950"
                                                max="2050"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="passing_month" className="block text-sm font-medium text-gray-700 mb-2">
                                                Passing Month *
                                            </label>
                                            <select
                                                id="passing_month"
                                                name="passing_month"
                                                value={currentCandidate.passing_month}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="">Select Month</option>
                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                                                    <option key={month} value={month}>{monthNames[month - 1]}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="contact_number" className="block text-sm font-medium text-gray-700 mb-2">
                                            Contact Number *
                                        </label>
                                        <input
                                            type="text"
                                            id="contact_number"
                                            name="contact_number"
                                            value={currentCandidate.contact_number}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="speciality" className="block text-sm font-medium text-gray-700 mb-2">
                                            Speciality *
                                        </label>
                                        <input
                                            type="text"
                                            id="speciality"
                                            name="speciality"
                                            value={currentCandidate.speciality}
                                            onChange={handleInputChange}
                                            required
                                            readOnly
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-100 cursor-not-allowed"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                                            State *
                                        </label>
                                        <input
                                            type="text"
                                            id="state"
                                            name="state"
                                            value={currentCandidate.state}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="current_city" className="block text-sm font-medium text-gray-700 mb-2">
                                            Current City *
                                        </label>
                                        <input
                                            type="text"
                                            id="current_city"
                                            name="current_city"
                                            value={currentCandidate.current_city}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="native_city" className="block text-sm font-medium text-gray-700 mb-2">
                                            Native City
                                        </label>
                                        <input
                                            type="text"
                                            id="native_city"
                                            name="native_city"
                                            value={currentCandidate.native_city}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="preferred_location" className="block text-sm font-medium text-gray-700 mb-2">
                                            Preferred Location
                                        </label>
                                        <input
                                            type="text"
                                            id="preferred_location"
                                            name="preferred_location"
                                            value={currentCandidate.preferred_location}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="spouse_speciality" className="block text-sm font-medium text-gray-700 mb-2">
                                            Spouse Speciality
                                        </label>
                                        <input
                                            type="text"
                                            id="spouse_speciality"
                                            name="spouse_speciality"
                                            value={currentCandidate.spouse_speciality}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="flex justify-end space-x-4 mt-6">
                                        <button
                                            type="button"
                                            onClick={() => setIsModalOpen(false)}
                                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                        >
                                            {modalMode === 'create' ? 'Create' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-3xl font-bold text-gray-800">Medical College Candidates</h2>
                    <button
                        onClick={() => openModal('create')}
                        className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition duration-300 ease-in-out"
                    >
                        + Create Candidate
                    </button>
                </div>
                {/* Filter Section */}
                <form onSubmit={(e) => e.preventDefault()} className="bg-white p-4 rounded-lg shadow-md mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-gray-700">Filter Candidates</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label htmlFor="filter_speciality" className="block text-sm font-medium text-gray-700 mb-1">
                                Speciality
                            </label>
                            <input
                                type="text"
                                id="filter_speciality"
                                name="speciality"
                                value={filters.speciality}
                                onChange={handleFilterChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label htmlFor="filter_medical_college" className="block text-sm font-medium text-gray-700 mb-1">
                                Medical College
                            </label>
                            <select
                                id="filter_medical_college"
                                name="medical_college"
                                value={filters.medical_college}
                                onChange={handleFilterChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Colleges</option>
                                {medicalColleges.map(college => (
                                    <option key={college.id} value={college.id}>
                                        {college.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="filter_passing_year" className="block text-sm font-medium text-gray-700 mb-1">
                                Passing Year
                            </label>
                            <input
                                type="number"
                                id="filter_passing_year"
                                name="passing_year"
                                value={filters.passing_year}
                                onChange={handleFilterChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label htmlFor="filter_state" className="block text-sm font-medium text-gray-700 mb-1">
                                State
                            </label>
                            <input
                                type="text"
                                id="filter_state"
                                name="state"
                                value={filters.state}
                                onChange={handleFilterChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label htmlFor="filter_current_city" className="block text-sm font-medium text-gray-700 mb-1">
                                Current City
                            </label>
                            <input
                                type="text"
                                id="filter_current_city"
                                name="current_city"
                                value={filters.current_city}
                                onChange={handleFilterChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label htmlFor="filter_preferred_location" className="block text-sm font-medium text-gray-700 mb-1">
                                Preferred Location
                            </label>
                            <input
                                type="text"
                                id="filter_preferred_location"
                                name="preferred_location"
                                value={filters.preferred_location}
                                onChange={handleFilterChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div className="flex items-end space-x-2 md:col-span-2">
                            <button
                                type="button"
                                onClick={handleFilter}
                                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                            >
                                Apply Filters
                            </button>
                            <button
                                type="button"
                                onClick={resetFilters}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </form>
                {/* Candidates Table */}
                {isLoading ? (
                    <div className="flex justify-center items-center h-64 bg-white rounded-lg shadow-md">
                        <p className="text-gray-600 text-lg">Loading candidates...</p>
                    </div>
                ) : candidates.length === 0 ? (
                    <div className="bg-gray-100 border border-gray-200 p-6 rounded-lg text-center">
                        <p className="text-gray-600">No candidates found. Create your first candidate!</p>
                    </div>
                ) : (
                    <div className="bg-white shadow-md rounded-lg overflow-hidden">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-blue-500 border-b">
                                    <th className="py-3 px-4 text-left text-white font-semibold">Name</th>
                                    <th className="py-3 px-4 text-left text-white font-semibold">Medical College</th>
                                    <th className="py-3 px-4 text-center text-white font-semibold">Speciality</th>
                                    <th className="py-3 px-4 text-center text-white font-semibold">Passing Year</th>
                                    <th className="py-3 px-4 text-left text-white font-semibold">Current City</th>
                                    <th className="py-3 px-4 text-left text-white font-semibold">Contact</th>
                                    <th className="py-3 px-4 text-right text-white font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidates.map((candidate) => (
                                    <React.Fragment key={candidate.id}>
                                        <tr className="border-b last:border-b-0 hover:bg-gray-50 transition duration-200">
                                            <td className="py-3 px-4 text-left">
                                                <span className="font-semibold">{candidate.name}</span>
                                            </td>
                                            <td className="py-3 px-4 text-left">
                                                {getCollegeName(candidate.medical_college)}
                                            </td>
                                            <td className="py-3 px-4 text-center">{candidate.speciality}</td>
                                            <td className="py-3 px-4 text-center">
                                                {monthNames[candidate.passing_month - 1]} {candidate.passing_year}
                                            </td>
                                            <td className="py-3 px-4 text-left">{candidate.current_city}, {candidate.state}</td>
                                            <td className="py-3 px-4 text-left">{candidate.contact_number}</td>
                                            <td className="py-3 px-4 text-right space-x-2">
                                                <button
                                                    onClick={() => toggleExpandedRow(candidate.id)}
                                                    className="text-green-500 hover:text-green-700 font-medium mr-2"
                                                >
                                                    {expandedCandidateId === candidate.id ? 'Hide' : 'Details'}
                                                </button>
                                                <button
                                                    onClick={() => openModal('edit', candidate)}
                                                    className="text-blue-500 hover:text-blue-700 font-medium mr-2"
                                                >
                                                    Edit
                                                </button>
                                                {/* Delete – visible only to the creator (matched by user_id) or superuser */}
                                                {(isSuperuser || isCreatedByMe(candidate)) && (
                                                    <button
                                                        onClick={() => handleDelete(candidate.id)}
                                                        className="text-red-500 hover:text-red-700 font-medium"
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {expandedCandidateId === candidate.id && (
                                            <tr>
                                                <td colSpan="7" className="p-4 bg-gray-50">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <h4 className="font-semibold text-gray-700">Native City:</h4>
                                                            <p className="text-gray-600">{candidate.native_city || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <h4 className="font-semibold text-gray-700">Preferred Location:</h4>
                                                            <p className="text-gray-600">{candidate.preferred_location || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <h4 className="font-semibold text-gray-700">Spouse Speciality:</h4>
                                                            <p className="text-gray-600">{candidate.spouse_speciality || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <h4 className="font-semibold text-gray-700">Entry Date:</h4>
                                                            <p className="text-gray-600">
                                                                {new Date(candidate.entry_date).toLocaleDateString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MedicalCollegeCandidateList;
